import { Fragment, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ProductDetailModal from './ProductDetailModal';
import NewProductPanel from './NewProductPanel';
import {
  productsApi,
  sellersApi,
  schedulesApi,
  type Product,
  type ProductStatus,
  type ProductUpdate,
  type ProductsListParams,
  type SellerListItem,
  type Schedule,
  type MissingField,
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { useBasePath, withBase } from '../lib/basePath';

const PAGE_SIZE = 100;

const MISSING_LABELS: Record<MissingField, string> = {
  name:              'название',
  price_rub:         'цену',
  bunch_size:        'упаковку',
  min_order_count:   'минимум',
  height:            'высоту',
  stock_or_schedule: 'наличие',
};

// ── small shared bits ─────────────────────────────────────────
function useClickOutside(ref: React.RefObject<HTMLElement>, onClose: () => void) {
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [ref, onClose]);
}

type ChipOption = { value: string; label: string; count?: number };

function FilterChip({ label, value, options, onChange }: {
  label: string; value: string; options: ChipOption[]; onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useClickOutside(ref, () => setOpen(false));
  const isOn = value !== options[0].value;
  const current = options.find((o) => o.value === value) || options[0];
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button type="button" className={'chip' + (isOn ? ' on' : '')} onClick={() => setOpen(!open)}>
        {label}{isOn ? ': ' + current.label : ''} <span className="caret">▾</span>
      </button>
      {open && (
        <div className="chip-menu">
          {options.map((o) => (
            <button key={o.value} type="button" className={o.value === value ? 'sel' : ''}
              onClick={() => { onChange(o.value); setOpen(false); }}>
              <span>{o.label}</span>
              {o.count !== undefined ? <span style={{ color: 'var(--muted)', fontSize: 12 }}>{o.count}</span> : null}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

function Switch({ on, onChange, title }: { on: boolean; onChange: () => void; title?: string }) {
  return (
    <button type="button" className={'switch' + (on ? ' on' : '')} role="switch" aria-checked={on}
      title={title || (on ? 'Товар виден покупателям' : 'Товар скрыт')} onClick={onChange}></button>
  );
}

function CellInput({ value, unit, min, width, onCommit }: {
  value: number | null; unit?: string; min?: number; width?: number; onCommit: (v: number | null) => void;
}) {
  const [val, setVal] = useState(value == null ? '' : String(value));
  useEffect(() => { setVal(value == null ? '' : String(value)); }, [value]);
  const commit = () => {
    const n = val === '' ? null : Math.max(min ?? 0, Number(val));
    if (n !== value) onCommit(n);
    setVal(n == null ? '' : String(n));
  };
  return (
    <span className="cellinput">
      <input type="number" min={min} value={val} placeholder="—" style={width ? { width } : undefined}
        onChange={(e) => setVal(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} />
      {unit ? <span className="unit">{unit}</span> : null}
    </span>
  );
}

function RowMenu({ onAction, basePath, productId }: {
  onAction: (a: 'open' | 'copy' | 'delete') => void; basePath: string; productId: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useClickOutside(ref, () => setOpen(false));
  const act = (a: 'open' | 'copy' | 'delete') => { setOpen(false); onAction(a); };
  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button type="button" className={'iconbtn' + (open ? ' open' : '')} title="Действия" onClick={() => setOpen(!open)}>⋯</button>
      {open && (
        <div className="row-menu">
          <button type="button" onClick={() => act('open')}>✎&ensp;Открыть карточку</button>
          <Link to={withBase(basePath, `/products/${productId}`)} className="row-menu-link" onClick={() => setOpen(false)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, color: 'var(--ink)', textDecoration: 'none' }}>
            ⤢&ensp;Полная страница
          </Link>
          <button type="button" onClick={() => act('copy')}>⧉&ensp;Создать копию</button>
          <hr />
          <button type="button" className="danger" onClick={() => act('delete')}>×&ensp;Удалить товар</button>
        </div>
      )}
    </span>
  );
}

// ── product meta helpers (map server model → mock fields) ──────
function isDraft(p: Product) { return p.status === 'draft'; }

type ShipWindow = { ship: string | null; deadline: string | null };

// Format an MSK ISO timestamp ("2026-05-21T10:00:00+03:00") as a short
// human label, always shown in Moscow time regardless of the viewer's TZ.
function fmtMsk(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      timeZone: 'Europe/Moscow', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

// «Поставка» cell: next shipment date + order-acceptance deadline pulled from
// the product's schedule. Clicking it opens the schedule for editing; products
// without a schedule get a muted affordance that opens the product card (where
// a schedule is attached).
function ShipCell({ p, win, basePath, onOpen }: {
  p: Product; win: ShipWindow | null | undefined; basePath: string; onOpen: () => void;
}) {
  if (p.schedule_group_id == null) {
    return (
      <button type="button" className="ship-cell ship-empty" title="Расписание не назначено — открыть карточку" onClick={onOpen}>
        + расписание
      </button>
    );
  }
  if (win === undefined) return <span className="muted" style={{ fontSize: 12 }}>…</span>;
  return (
    <Link to={withBase(basePath, `/schedules/${p.schedule_group_id}`)} className="ship-cell"
      title="Открыть расписание для редактирования дат">
      <span className="ship-ship">📦 {win && win.ship ? fmtMsk(win.ship) : 'нет дат'}</span>
      {win && win.deadline ? <span className="ship-deadline">приём до {fmtMsk(win.deadline)}</span> : null}
    </Link>
  );
}

function ProductRow({ p, basePath, isAdmin, sellerLabel, win, selected, onSelect, onPatch, onOpen, onCopy, onDelete, flash, savedAt }: {
  p: Product; basePath: string; isAdmin: boolean; sellerLabel: (id: number) => string;
  win: ShipWindow | null | undefined;
  selected: boolean; onSelect: () => void;
  onPatch: (fields: ProductUpdate) => void;
  onOpen: () => void; onCopy: () => void; onDelete: () => void;
  flash: boolean; savedAt: number | null;
}) {
  const height = p.properties?.height != null ? String(p.properties.height) : null;
  const missing = p.missing_required || [];
  return (
    <tr id={`product-row-${p.id}`} className={(selected ? 'selected' : '') + (flash ? ' flash' : '')}>
      <td style={{ width: 36 }}>
        <input type="checkbox" className="ck" checked={selected} onChange={onSelect} />
      </td>
      <td>
        <div className="prod-cell">
          <span className={'thumb' + (p.images.length ? ' filled' : '')}
            title={p.images.length ? p.name : 'Загрузить фото'} onClick={onOpen}>
            {p.images[0] ? <img src={p.images[0]} alt="" /> : <span className="plus">+</span>}
          </span>
          <span className="prod-name">
            <a onClick={onOpen}>{p.name}</a>
            <span className="prod-meta">
              <code>{p.external_offer_id}</code>
              {height ? <span>{height} см</span> : null}
              {isAdmin ? <span>{sellerLabel(p.seller_id)}</span> : null}
              {isDraft(p) && missing.length
                ? <span className="badge draft">черновик — нет {missing.map((m) => MISSING_LABELS[m]).join(', ')}</span>
                : null}
            </span>
          </span>
        </div>
      </td>
      <td className="num"><CellInput value={p.price_rub || null} unit="₽" min={0} onCommit={(v) => { if (v != null) onPatch({ price_rub: v }); }} /></td>
      <td className="num"><CellInput value={p.stock_qty} min={0} onCommit={(v) => onPatch({ stock_qty: v })} /></td>
      <td className="num"><CellInput value={p.bunch_size} unit="шт" min={1} onCommit={(v) => onPatch({ bunch_size: v })} /></td>
      <td className="num"><CellInput value={p.min_order_count} min={1} width={48} onCommit={(v) => onPatch({ min_order_count: v ?? 1 })} /></td>
      <td><ShipCell p={p} win={win} basePath={basePath} onOpen={onOpen} /></td>
      <td style={{ width: 60 }}>
        <Switch on={p.is_active} onChange={() => onPatch({ is_active: !p.is_active })} />
      </td>
      <td style={{ width: 130 }}>
        <div className="rowact">
          {savedAt ? <span className="saved-tag" key={savedAt}>✓ Сохранено</span> : null}
          <RowMenu basePath={basePath} productId={p.id}
            onAction={(a) => a === 'open' ? onOpen() : a === 'copy' ? onCopy() : onDelete()} />
        </div>
      </td>
    </tr>
  );
}

export default function ProductsList() {
  const { user } = useAuth();
  const base = useBasePath();
  const isAdmin = user?.role === 'admin';
  const [searchParams] = useSearchParams();

  const [filterSeller, setFilterSeller] = useState<number | ''>(() => {
    const s = searchParams.get('seller_id'); const n = Number(s);
    return s && Number.isFinite(n) && n > 0 ? n : '';
  });
  const [filterActive, setFilterActive] = useState<'all' | 'on' | 'off'>('all');
  const [filterSchedule, setFilterSchedule] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState<'all' | ProductStatus>(() => {
    const st = searchParams.get('status'); return st === 'draft' || st === 'ready' ? st : 'all';
  });
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sort, setSort] = useState<{ key: 'none' | 'name' | 'price' | 'stock'; dir: 1 | -1 }>({ key: 'none', dir: 1 });
  const [offset, setOffset] = useState(0);

  const [sellers, setSellers] = useState<SellerListItem[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  // Next shipment window per schedule group (lazy preview, cached). undefined
  // = not yet fetched (shows «…»), null = no window / error.
  const [windows, setWindows] = useState<Record<number, ShipWindow | null>>({});
  const [items, setItems] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Monotonically increasing counter used to discard stale responses when a
  // newer request has already been dispatched (race-condition guard).
  const reloadGen = useRef(0);

  const [savedAt, setSavedAt] = useState<Record<number, number>>({});
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [flashId, setFlashId] = useState<number | null>(null);

  const effectiveSellerId = isAdmin ? (typeof filterSeller === 'number' ? filterSeller : null) : (user?.id ?? null);

  // Debounce the search query by 300 ms to avoid firing a request on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(id);
  }, [query]);

  // toast auto-dismiss
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(id);
  }, [toast]);

  // master checkbox tri-state — driven by the .ck.semi class (the design's
  // styled half-check), since the inputs use appearance:none and the native
  // indeterminate look would be invisible.
  const allSelected = items.length > 0 && items.every((p) => selected.has(p.id));
  const someSelected = items.some((p) => selected.has(p.id));

  // smooth scroll + flash on new/copy
  useEffect(() => {
    if (flashId == null) return;
    const el = document.getElementById(`product-row-${flashId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const tm = setTimeout(() => setFlashId(null), 1700);
      return () => clearTimeout(tm);
    }
  }, [flashId, items]);

  useEffect(() => {
    if (isAdmin) sellersApi.list().then((r) => setSellers(r.sellers)).catch(() => { /* noop */ });
  }, [isAdmin]);

  // Schedules for the «Расписание» filter chip (scoped to the active seller).
  useEffect(() => {
    schedulesApi.list(effectiveSellerId ?? undefined)
      .then((r) => setSchedules(r.schedules))
      .catch(() => setSchedules([]));
  }, [effectiveSellerId]);

  // Lazy-load the next shipment window for every schedule referenced on the
  // page (one preview per distinct schedule, cached across reloads).
  useEffect(() => {
    const ids = [...new Set(items.map((p) => p.schedule_group_id).filter((x): x is number => x != null))];
    const missing = ids.filter((id) => !(id in windows));
    if (missing.length === 0) return;
    Promise.all(missing.map((id) =>
      schedulesApi.preview(id, 1)
        .then((r) => [id, r.windows[0] ?? null] as const)
        .catch(() => [id, null] as const),
    )).then((pairs) => setWindows((prev) => {
      const next = { ...prev };
      for (const [id, w] of pairs) next[id] = w;
      return next;
    }));
  }, [items, windows]);

  function reload() {
    const gen = ++reloadGen.current;
    setLoading(true);
    const params: ProductsListParams = { limit: PAGE_SIZE, offset };
    if (filterSeller !== '') params.seller_id = filterSeller;
    if (filterSchedule !== '') params.schedule_group_id = filterSchedule;
    if (filterActive === 'on') params.is_active = true;
    if (filterActive === 'off') params.is_active = false;
    if (filterStatus !== 'all') params.status = filterStatus;
    if (debouncedQuery.trim()) params.q = debouncedQuery.trim();
    productsApi.list(params)
      .then((r) => {
        if (gen !== reloadGen.current) return; // discard stale response
        setItems(r.products);
        setTotal(r.total);
        setErr(null);
        setSelected((s) => new Set([...s].filter((id) => r.products.some((p) => p.id === id))));
      })
      .catch((e) => { if (gen === reloadGen.current) setErr(String(e?.message ?? e)); })
      .finally(() => { if (gen === reloadGen.current) setLoading(false); });
  }
  useEffect(reload, [filterSeller, filterSchedule, filterActive, filterStatus, debouncedQuery, offset]);  // eslint-disable-line react-hooks/exhaustive-deps

  // client-side sort of the loaded page
  const sorted = (() => {
    if (sort.key === 'none') return items;
    const arr = [...items];
    if (sort.key === 'name') arr.sort((a, b) => a.name.localeCompare(b.name, 'ru') * sort.dir);
    if (sort.key === 'price') arr.sort((a, b) => ((a.price_rub ?? -1) - (b.price_rub ?? -1)) * sort.dir);
    if (sort.key === 'stock') arr.sort((a, b) => ((a.stock_qty ?? -1) - (b.stock_qty ?? -1)) * sort.dir);
    return arr;
  })();

  const hasFilters = query || filterSeller !== '' || filterSchedule !== '' || filterActive !== 'all' || filterStatus !== 'all';
  const resetFilters = () => { setQuery(''); setFilterSeller(''); setFilterSchedule(''); setFilterActive('all'); setFilterStatus('all'); setOffset(0); };

  function sellerLabel(id: number) {
    const s = sellers.find((x) => x.id === id);
    return s ? (s.display_name || s.login) : `#${id}`;
  }

  // ---- per-row autosave patch ----
  async function patchRow(p: Product, fields: ProductUpdate) {
    try {
      const r = await productsApi.update(p.id, fields);
      setItems((prev) => prev.map((x) => x.id === p.id ? r.product : x));
      setSavedAt((m) => ({ ...m, [p.id]: Date.now() }));
      setTimeout(() => setSavedAt((m) => { const n = { ...m }; delete n[p.id]; return n; }), 1800);
    } catch (e) {
      alert(`Не сохранилось: ${String((e as Error)?.message ?? e)}`);
    }
  }

  // ---- selection ----
  const toggleAll = () => {
    const all = items.length > 0 && items.every((p) => selected.has(p.id));
    setSelected(all ? new Set() : new Set(items.map((p) => p.id)));
  };
  const toggleOne = (id: number) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());

  const clickSort = (key: 'name' | 'price' | 'stock') =>
    setSort((s) => s.key === key ? { key, dir: (-s.dir) as 1 | -1 } : { key, dir: 1 });
  const sortMark = (key: string) => (sort.key === key ? (sort.dir === 1 ? ' ↑' : ' ↓') : '');

  // ---- row actions ----
  async function rowDelete(p: Product) {
    if (!confirm(`Удалить «${p.name}» (${p.external_offer_id})? Это действие нельзя отменить.`)) return;
    try {
      await productsApi.remove(p.id);
      setItems((prev) => prev.filter((x) => x.id !== p.id));
      setTotal((t) => Math.max(0, t - 1));
      setSelected((s) => { const n = new Set(s); n.delete(p.id); return n; });
      setToast(`«${p.name}» удалён`);
    } catch (e) { alert(`Не удалось удалить: ${String((e as Error)?.message ?? e)}`); }
  }
  async function rowCopy(p: Product) {
    try {
      const r = await productsApi.duplicate(p.id);
      setItems((prev) => {
        const i = prev.findIndex((x) => x.id === p.id);
        if (i < 0) return [r.product, ...prev];
        return [...prev.slice(0, i + 1), r.product, ...prev.slice(i + 1)];
      });
      setTotal((t) => t + 1);
      setFlashId(r.product.id);
      setToast('Копия создана — проверьте артикул и цену');
    } catch (e) { alert(`Не удалось скопировать: ${String((e as Error)?.message ?? e)}`); }
  }

  // ---- bulk ----
  // Field-picker: choose what to change for the whole selection. «Цена ±%» is
  // a per-product percentage (individual updates); everything else is an
  // absolute value applied atomically via the bulk endpoint.
  type BulkField = 'price_pct' | 'price_set' | 'stock_qty' | 'min_order_count' | 'bunch_size' | 'discount_percent' | 'schedule_group_id';
  const [bulkField, setBulkField] = useState<BulkField>('price_pct');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const BULK_LABELS: Record<BulkField, string> = {
    price_pct: 'Цена ±%', price_set: 'Цена, ₽', stock_qty: 'Наличие',
    min_order_count: 'Мин. заказ', bunch_size: 'В упаковке', discount_percent: 'Скидка, %',
    schedule_group_id: 'Расписание',
  };

  async function bulkPricePercent(ids: number[]) {
    const pct = Number(bulkValue);
    if (!pct) return;
    const targets = ids.map((id) => items.find((x) => x.id === id)).filter((p): p is Product => !!p && !!p.price_rub);
    if (targets.length === 0) { setToast('У выбранных товаров нет цены для изменения'); return; }
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(targets.map((p) =>
        productsApi.update(p.id, { price_rub: Math.round(p.price_rub * (1 + pct / 100)) })));
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      setBulkValue('');
      setToast(`Цены изменены на ${pct > 0 ? '+' : ''}${pct}% у ${ok} товаров${failed ? `; ошибок: ${failed}` : ''}`);
    } catch (e) { alert(`Ошибка: ${String((e as Error)?.message ?? e)}`); }
    finally { setBulkBusy(false); reload(); }
  }

  async function applyBulk() {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (bulkField === 'price_pct') { await bulkPricePercent(ids); return; }
    const v = bulkValue.trim();
    const num = v === '' ? null : Number(v);
    const patch: ProductUpdate = {};
    switch (bulkField) {
      case 'price_set':        if (num == null || num < 0) return; patch.price_rub = Math.round(num); break;
      case 'stock_qty':        patch.stock_qty = num == null ? null : Math.max(0, num); break;
      case 'min_order_count':  patch.min_order_count = num == null ? null : Math.max(1, num); break;
      case 'bunch_size':       patch.bunch_size = num == null ? null : Math.max(1, num); break;
      case 'discount_percent': patch.discount_percent = num == null ? null : Math.max(0, Math.min(99, num)); break;
      case 'schedule_group_id': patch.schedule_group_id = num; break;
    }
    setBulkBusy(true);
    try {
      const r = await productsApi.bulk(ids, patch);
      setBulkValue('');
      setToast(`«${BULK_LABELS[bulkField]}» применено: ${r.updated} из ${ids.length}${r.errors.length ? `; ошибок: ${r.errors.length}` : ''}`);
    } catch (e) { alert(`Ошибка: ${String((e as Error)?.message ?? e)}`); }
    finally { setBulkBusy(false); reload(); }
  }
  async function bulkActive(active: boolean) {
    const ids = [...selected];
    setBulkBusy(true);
    try {
      await productsApi.bulk(ids, { is_active: active });
      setToast(active ? `Показано покупателям: ${ids.length}` : `Скрыто: ${ids.length}`);
      reload();
    } catch (e) { alert(`Ошибка: ${String((e as Error)?.message ?? e)}`); }
    finally { setBulkBusy(false); }
  }
  async function bulkDelete() {
    const ids = [...selected];
    if (!confirm(`Удалить ${ids.length} товаров? Это действие нельзя отменить.`)) return;
    setBulkBusy(true);
    try {
      const r = await productsApi.bulkDelete(ids);
      setToast(`Удалено товаров: ${r.deleted}`);
      clearSel();
      reload();
    } catch (e) { alert(`Ошибка: ${String((e as Error)?.message ?? e)}`); }
    finally { setBulkBusy(false); }
  }

  return (
    <div className="cab-page">
      <div className="page-head">
        <h1>Товары</h1>
        <span className="count">{total} позиций</span>
        <span className="spacer"></span>
        <button type="button" className="btn primary"
          onClick={() => { if (effectiveSellerId == null) { alert('Выберите продавца в фильтре, чтобы добавить товар.'); return; } setShowNew(true); }}>
          + Новый товар
        </button>
      </div>

      <div className="toolbar">
        <span className="searchbox">
          <span>⌕</span>
          <input placeholder="Артикул или название" value={query} onChange={(e) => { setOffset(0); setQuery(e.target.value); }} />
        </span>
        {isAdmin && (
          <FilterChip label="Продавец" value={filterSeller === '' ? 'all' : String(filterSeller)}
            onChange={(v) => { setOffset(0); setFilterSeller(v === 'all' ? '' : Number(v)); }}
            options={[{ value: 'all', label: 'Все' }, ...sellers.map((s) => ({ value: String(s.id), label: s.display_name || s.login }))]} />
        )}
        <FilterChip label="Активность" value={filterActive}
          onChange={(v) => { setOffset(0); setFilterActive(v as 'all' | 'on' | 'off'); }}
          options={[{ value: 'all', label: 'Все' }, { value: 'on', label: 'Активные' }, { value: 'off', label: 'Скрытые' }]} />
        <FilterChip label="Статус" value={filterStatus}
          onChange={(v) => { setOffset(0); setFilterStatus(v as 'all' | ProductStatus); }}
          options={[{ value: 'all', label: 'Все' }, { value: 'ready', label: 'Готовы к публикации' }, { value: 'draft', label: 'Черновики' }]} />
        {schedules.length > 0 && (
          <FilterChip label="Расписание" value={filterSchedule === '' ? 'all' : String(filterSchedule)}
            onChange={(v) => { setOffset(0); setFilterSchedule(v === 'all' ? '' : Number(v)); }}
            options={[{ value: 'all', label: 'Все' }, ...schedules.map((s) => ({ value: String(s.id), label: s.name }))]} />
        )}
        {hasFilters ? <button type="button" className="btn ghost reset" onClick={resetFilters}>Сбросить</button> : null}
        <span style={{ flex: 1 }}></span>
        <span style={{ color: 'var(--muted)', fontSize: 13 }}>Показано {items.length} из {total}</span>
      </div>

      {err && <div className="error">{err}</div>}
      {loading && <p className="muted">Загрузка…</p>}

      <div className="table-card">
        <table className="products">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input type="checkbox" className={'ck' + (someSelected && !allSelected ? ' semi' : '')}
                  checked={allSelected} onChange={toggleAll} disabled={items.length === 0} />
              </th>
              <th className="sortable" onClick={() => clickSort('name')}>Товар{sortMark('name')}</th>
              <th className="num sortable" onClick={() => clickSort('price')}>Цена, ₽{sortMark('price')}</th>
              <th className="num sortable" onClick={() => clickSort('stock')}>Наличие{sortMark('stock')}</th>
              <th className="num">В упаковке</th>
              <th className="num">Мин. заказ</th>
              <th>Поставка</th>
              <th>Активен</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p) => (
              <Fragment key={p.id}>
                <ProductRow
                  p={p} basePath={base} isAdmin={isAdmin} sellerLabel={sellerLabel}
                  win={p.schedule_group_id == null ? null : windows[p.schedule_group_id]}
                  selected={selected.has(p.id)} onSelect={() => toggleOne(p.id)}
                  onPatch={(fields) => patchRow(p, fields)}
                  onOpen={() => setEditingId(p.id)} onCopy={() => rowCopy(p)} onDelete={() => rowDelete(p)}
                  flash={p.id === flashId} savedAt={savedAt[p.id] ?? null}
                />
              </Fragment>
            ))}
          </tbody>
        </table>
        {!loading && items.length === 0 && (
          <div className="cab-empty">
            <b>Ничего не нашлось</b>
            Попробуйте изменить поиск или сбросить фильтры
          </div>
        )}
      </div>

      <div className="row" style={{ marginTop: 12, justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: 'var(--muted)', fontSize: 13 }}>{total === 0 ? 0 : offset + 1}–{Math.min(offset + items.length, total)} из {total}</span>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>← Назад</button>
          <button className="btn" disabled={offset + items.length >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Вперёд →</button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="bulkbar">
          <span className="cnt">Выбрано: {selected.size}</span>
          <span className="bulk-price">
            <select className="bulk-select" value={bulkField}
              onChange={(e) => { setBulkField(e.target.value as BulkField); setBulkValue(''); }}>
              {(Object.keys(BULK_LABELS) as BulkField[]).map((k) => (
                <option key={k} value={k}>{BULK_LABELS[k]}</option>
              ))}
            </select>
            {bulkField === 'schedule_group_id' ? (
              <select className="bulk-select" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}>
                <option value="">— нет —</option>
                {schedules.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            ) : (
              <input type="number" value={bulkValue}
                placeholder={bulkField === 'price_pct' ? '+10' : bulkField === 'min_order_count' ? '1' : '—'}
                onChange={(e) => setBulkValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') applyBulk(); }} />
            )}
            <button type="button" className="btn" disabled={bulkBusy} onClick={applyBulk}>OK</button>
          </span>
          <button type="button" className="btn" disabled={bulkBusy} onClick={() => bulkActive(true)}>Показать</button>
          <button type="button" className="btn" disabled={bulkBusy} onClick={() => bulkActive(false)}>Скрыть</button>
          <button type="button" className="btn danger" disabled={bulkBusy} onClick={bulkDelete}>Удалить</button>
          <button type="button" className="close" title="Снять выделение" onClick={clearSel}>✕</button>
        </div>
      )}

      {editingId != null && (
        <ProductDetailModal id={editingId} onClose={() => setEditingId(null)}
          onChanged={(p) => setItems((prev) => prev.map((x) => x.id === p.id ? p : x))} />
      )}

      {showNew && effectiveSellerId != null && (
        <NewProductPanel sellerId={effectiveSellerId} onClose={() => setShowNew(false)}
          onCreated={(p, again) => {
            setItems((prev) => [p, ...prev]);
            setTotal((t) => t + 1);
            setFlashId(p.id);
            setToast(`«${p.name}» добавлен` + (p.is_active ? ' и виден покупателям' : ' как скрытый'));
            if (!again) setShowNew(false);
          }} />
      )}

      {toast && <div className="cab-toast"><span className="tick">✓</span>{toast}</div>}
    </div>
  );
}
